import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  useForm,
  FormProvider,
  useFieldArray,
  useWatch,
} from "react-hook-form";
import { filterTypes, resourceTypes } from "../../../Findings/FindingsUtils";
import {
  LS_SCOPE,
  readOnlyFields,
  nonSelectableScopesFields,
} from "../../../Findings/FindingsFilters/FilterUtils";
import { ScopesContext, SelectedScope } from "../ScopeProvider/ScopeProvider";
import type { TreeNode } from "../../../Components/Tree/types";
import "./ScopeSettings.scss";
import { showToast } from "../../../Forms/Dialogs/Toast/Toast";
import {
  SAVE_SCOPE,
  UPDATE_SCOPE,
} from "../../../Findings/FindingsFilters/FindingsFilterApi";
import {
  getQueuePluralsByCount,
  getScopeJson,
  processScope,
} from "../../AppViewUtils";
import { useTranslation } from "react-i18next";
import FilterSaveAs from "../../../Findings/FindingsFilters/FilterSaveAs/FilterSaveAs";
import ConditionRowsPanel from "../../FiltersBar/ConditionRowsPanel/ConditionRowsPanel";
import useFiltersData from "../../useFiltersData";
import FilterInQueuePopup from "../../../Findings/FindingsFilters/FilterInQueuePopup/FilterInQueuePopup";
import {
  AppPreferencesContext,
  userActionsEnum,
} from "../../../General/AppPreferencesProvider";
import { AppPreferencesContextType } from "../../../types/AppPreferencesProvider";
import {
  useUserAnalytics,
  useMutationWrapper,
  ModalContext,
  showDialog,
  REQUIRED_ERROR_MESSAGE,
} from "@SeemplicityDev/shared";
import {
  FieldData,
  FilterPanel,
} from "../../FilterManagement/CommonFilterTypes";
import { MutationTuple } from "@apollo/client";
import useUsedFields from "../../useUsedFields";
import { ScopeManagementContext } from "../../../Findings/FindingsFilters/ScopeHierarchy/ScopeManagement/ScopeManagementContext";

interface ScopeFormData {
  scopeName: string;
  filterPanels: FilterPanel[];
  location: any;
  saveAsFilterName?: string;
  saveAsLocation?: any;
}

interface ScopeMutationData {
  name: string;
  filterData: string;
  filterType: string;
  scope_group: string;
  id?: string;
  overwrite?: boolean;
}

interface ScopeSettingsProps {
  // Set to true in contexts that do not have an Apply button (e.g. Rules /
  // Automation Wizard / Remediation Queue scope inputs), where Save/Save As
  // must remain enabled even when pending unapplied changes exist.
  allowSaveWithPendingChanges?: boolean;
}

const ScopeSettings: React.FC<ScopeSettingsProps> = ({
  allowSaveWithPendingChanges = false,
}) => {
  const { isAllowed } = useContext(
    AppPreferencesContext
  ) as AppPreferencesContextType;

  const { t } = useTranslation();

  const methods = useForm<ScopeFormData>({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: {
      scopeName: "",
      filterPanels: [{ conditions: [] }],
      location: null,
    },
  });
  const [isSaving, setIsSaving] = useState(false);
  const { usedFields, addUsedField, removeUsedField, resetUsedFields } =
    useUsedFields();

  const { trackEvent } = useUserAnalytics();

  const { openModal, closeModal } = useContext(ModalContext);

  const [updateScope] = useMutationWrapper(UPDATE_SCOPE, {
    refetchQueries: ["scopes"],
  }) as MutationTuple<any, any>;

  const [createScope] = useMutationWrapper(SAVE_SCOPE, {
    refetchQueries: ["scopes"],
  }) as MutationTuple<any, any>;

  const {
    handleSubmit,
    control,
    formState: { isValid, isDirty },
    reset,
    getValues,
    setValue,
    setError,
  } = methods;
  const {
    scopesState: {
      scopesList,
      defaultScope,
      selectedScope,
      isNewScope,
      shouldBlinkField,
      scopeToExecute,
      pendingScopeToExecute,
      isDirty: isScopeDirty,
    },
    updateScopeState,
    searchTree,
  } = useContext(ScopesContext);
  const { setDefaultsForScopeAndFilter } = useContext(ScopeManagementContext);
  const { fields, append, remove } = useFieldArray({
    control,
    name: "filterPanels",
  });

  const watchFieldArray = useWatch({
    control,
    name: `filterPanels`,
  });

  const controlledFields = fields.map((field, index) => {
    return {
      ...field,
      ...watchFieldArray[index],
    };
  });

  const { fieldsData: filterFieldsData } = useFiltersData({
    filterType: filterTypes.SCOPE,
  });

  useEffect(() => {
    const clearUp = setTimeout(() => {
      updateScopeState({ isDirty });
    }, 0);

    return () => clearTimeout(clearUp);
  }, [isDirty, updateScopeState]);

  const resetNewScope = () => {
    let location = scopesList?.[0];
    if (selectedScope) {
      const locationToSearch =
        selectedScope?.value?.type === resourceTypes.GROUP
          ? selectedScope?.value?.id
          : selectedScope?.parentId;

      if (locationToSearch && !!scopesList?.[0]) {
        location =
          searchTree(scopesList?.[0]!, locationToSearch) || scopesList?.[0];
      }
    }

    let filterPanels: FilterPanel[] = [{ conditions: [] }];

    // Handles in/out value addition to "new/unsaved scope"
    if (!!scopeToExecute || !!pendingScopeToExecute) {
      const filter = selectedScope?.value?.query
        ? JSON.parse(selectedScope?.value?.query)
        : null;

      if (filter) {
        const processedFilter = processScope(filter);
        filterPanels = processedFilter?.scopeConditions;
      }
    }

    if (isScopeDirty) {
      setValue("filterPanels", filterPanels, { shouldDirty: true });
    } else {
      reset({
        ...getValues(),
        scopeName: "",
        filterPanels,
        location,
      });
    }
  };

  const resetExistingScope = () => {
    const location = selectedScope?.parentId
      ? searchTree(scopesList?.[0]!, selectedScope.parentId)
      : null;
    let filterPanels: FilterPanel[] = [];

    const filter = selectedScope?.value?.query
      ? JSON.parse(selectedScope?.value?.query)
      : null;

    if (filter) {
      const processedFilter = processScope(filter);
      filterPanels = processedFilter?.scopeConditions;
    }

    if (selectedScope?.value?.isDirty) {
      setValue("filterPanels", filterPanels, { shouldDirty: true });
    } else {
      reset({
        ...getValues(),
        scopeName: selectedScope?.label || "",
        filterPanels,
        location: location || undefined,
      });
    }

    updateScopeState({
      scopeToExecute: selectedScope?.value?.query,
      pendingScopeToExecute: undefined,
    });
  };

  // Removing the only condition of the only row leaves nothing to scope by, so
  // fall back to the default scope. Clearing isNewScope/isDirty is what lets
  // the skipped backend queries fire again and drops the "unsaved" labelling.
  const resetToDefaultScope = () => {
    const defaultQuery = defaultScope?.value?.query ?? null;

    updateScopeState({
      selectedScope: defaultScope,
      scopeToExecute: defaultQuery,
      pendingScopeToExecute: null,
      isNewScope: false,
      isDirty: false,
    });

    // Only the app header owns the persisted scope; modal contexts (Rules /
    // Automation / Add To Scope) must not overwrite it.
    if (defaultScope && setDefaultsForScopeAndFilter) {
      localStorage.setItem(
        LS_SCOPE,
        JSON.stringify({ ...defaultScope, scopeToExecute: defaultQuery })
      );
    }
  };

  // reset new scope
  useEffect(() => {
    if (isNewScope) {
      resetNewScope();
      resetUsedFields();
    }
  }, [isNewScope, scopesList, selectedScope]);

  // reset existing scope
  useEffect(() => {
    if (!isNewScope && selectedScope) {
      resetExistingScope();
      resetUsedFields();
    }
  }, [scopesList, selectedScope]);

  const previewFilter = () => {
    const data = getValues();
    const scopeJson = getScopeJson(data);
    const pendingScopeToExecute =
      scopeJson && scopeJson !== "{}" ? scopeJson : null;

    // Pending changes do not trigger backend queries or write to localStorage —
    // those only happen on Apply.
    updateScopeState({ pendingScopeToExecute });
  };

  const onCreate = useCallback(
    (data: ScopeFormData) => {
      let scopeMutationData: Partial<ScopeMutationData> = {};
      const scope = getScopeJson(data);
      scopeMutationData = {
        name: data?.saveAsFilterName || data?.scopeName,
        filterData: scope,
        filterType: filterTypes.SCOPE,
        scope_group: data?.saveAsLocation?.id || data?.location?.id,
      };

      // set the level for the breadcrumbs
      const level =
        data?.saveAsLocation?.level > -1
          ? data?.saveAsLocation?.level + 1
          : selectedScope?.level;

      createScope({ variables: scopeMutationData })
        .then((res) => {
          if (!!res.data?.new_finding_scope_v2?.finding_filter) {
            const newSelectedScope: SelectedScope = {
              label: scopeMutationData.name!,
              level,
              value: {
                query: scope,
                id: res.data?.new_finding_scope_v2?.finding_filter?.id,
                editable: true,
                type: "scope",
                isDirty: false,
              },
              parentId: scopeMutationData?.scope_group,
            };

            updateScopeState({
              scopeToExecute: scope,
              pendingScopeToExecute: undefined,
              selectedScope: newSelectedScope,
              isNewScope: false,
              isDirty: false,
            });
            localStorage.setItem(LS_SCOPE, JSON.stringify(newSelectedScope));

            closeModal();
            showToast({
              message: (
                <div className={"modal-text"}>
                  The Scope{" "}
                  <span className={"modal-live-text"}>
                    {newSelectedScope.label}
                  </span>{" "}
                  was created successfully
                </div>
              ),
              "data-testid": "scope-creation-success-toast",
            });
          } else {
            showDialog({
              title: "Error",
              type: "error",
              message: "Scope creation failed",
              buttons: [
                {
                  label: "Close",
                },
              ],
            });
          }
        })
        .catch((err: Error) => {
          showDialog({
            title: "Error",
            type: "error",
            message: err.message,
            buttons: [
              {
                label: "Close",
              },
            ],
          });
        });
    },
    [closeModal, createScope, setError, t, updateScopeState, selectedScope]
  );

  const saveScope = () => {
    if (isNewScope || !selectedScope?.value?.id) {
      handleSubmit(onCreate)();
    } else {
      trackEvent("Save_Scope");
      handleSubmit((data: ScopeFormData) => onUpdate(data, false))();
    }
  };

  const onUpdate = (data: ScopeFormData, overwrite: boolean) => {
    if (!data?.scopeName) {
      setError("scopeName", {
        type: "error",
        message: t(REQUIRED_ERROR_MESSAGE),
      });
      return;
    }
    let scopeMutationData: ScopeMutationData;
    const scope = getScopeJson(data);

    const level = selectedScope?.level;
    scopeMutationData = {
      name: data?.scopeName,
      filterData: scope,
      filterType: filterTypes.SCOPE,
      scope_group: data?.location?.id,
      id: selectedScope?.value?.id,
      overwrite,
    };

    setIsSaving(true);
    updateScope({ variables: scopeMutationData })
      .then((res) => {
        if (
          !overwrite &&
          !res?.data?.update_finding_scope_v2?.ok &&
          res?.data?.update_finding_scope_v2?.effected_queues?.length
        ) {
          showDialog({
            title: "Save Scope",
            type: "warning",
            className: "save-warning",
            alignLeft: true,
            description: (
              <div>
                The Scope{" "}
                <span className={"modal-live-text"}>"{data?.scopeName}"</span>{" "}
                is part of{" "}
                {getQueuePluralsByCount(
                  res?.data?.update_finding_scope_v2?.effected_queues?.length ||
                    0
                ) === "queue"
                  ? " an existing queue"
                  : " existing queues"}
                .
              </div>
            ),
            message: (
              <FilterInQueuePopup
                type={"scope"}
                queuesList={res?.data?.update_finding_scope_v2?.effected_queues?.map(
                  (queue: string) => ({ name: queue })
                )}
              />
            ),
            buttons: [
              {
                buttonStyle: "textLink",
                label: "Cancel",
              },
              {
                label: "Save As New Scope",
                onClick: () => {
                  openSaveAsModal();
                },
              },
              {
                buttonStyle: "primary",
                label: "Save and Update Queue",
                onClick: () => {
                  handleSubmit((data: ScopeFormData) => onUpdate(data, true))();
                },
              },
            ],
          });
        } else if (!!res.data?.update_finding_scope_v2?.ok) {
          const updatedSelectedScope: SelectedScope = {
            level,
            label: scopeMutationData.name,
            value: {
              children: undefined,
              query: scope,
              id: scopeMutationData.id,
              editable: true,
              type: "scope",
              isDirty: false,
            },
            parentId: scopeMutationData?.scope_group,
          };

          updateScopeState({
            scopeToExecute: scope,
            pendingScopeToExecute: undefined,
            selectedScope: updatedSelectedScope,
            isDirty: false,
          });
          localStorage.setItem(LS_SCOPE, JSON.stringify(updatedSelectedScope));

          showToast({
            message: (
              <div className={"modal-text"}>
                The Scope{" "}
                <span className={"modal-live-text"}>
                  {updatedSelectedScope.label}
                </span>{" "}
                was updated successfully
              </div>
            ),
            "data-testid": "scope-update-success-toast",
          });
        } else {
          showDialog({
            title: "Error",
            type: "error",
            message: "Scope update failed",
            buttons: [
              {
                label: "Close",
              },
            ],
          });
        }
      })
      .catch((err: Error) => {
        showDialog({
          title: "Error",
          type: "error",
          message: err.message,
          buttons: [
            {
              label: "Close",
            },
          ],
        });
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const openSaveAsModal = () => {
    const location =
      searchTree(scopesList?.[0]!, selectedScope?.parentId!) || scopesList?.[0];

    openModal({
      title: "Save As",
      size: "m",
      overlay: false,
      component: (
        <FilterSaveAs
          location={location as unknown as TreeNode}
          type={resourceTypes.SCOPE}
          scopesList={scopesList as unknown as TreeNode[] | null}
          setValue={setValuesAndSave}
          buttonText={"Save Scope"}
          placeholder={"Scope Name"}
          filter_data={pendingScopeToExecute ?? scopeToExecute}
          name={selectedScope?.label || ""}
        />
      ),
    });
  };

  const setValuesAndSave = (data: ScopeFormData) => {
    trackEvent("Save_As_Scope");
    setValue("saveAsFilterName", data?.saveAsFilterName);
    setValue("saveAsLocation", data?.saveAsLocation);
    handleSubmit((data) => onCreate(data))();
  };

  const addCondition = () => {
    trackEvent("Scope_Add_Condition");
    append({
      conditions: [{ field: null, condition: null, value: "" }],
    });
  };

  const noConditions = React.useMemo(
    () =>
      controlledFields?.length <= 1 &&
      !controlledFields?.[0]?.conditions?.length,
    [controlledFields]
  );

  // Disable Save/Save As while there are pending unapplied changes — the user
  // must Apply (and thus review) before persisting a scope configuration.
  // Contexts without an Apply button (Rules / Automation Wizard / Remediation
  // Queue scope inputs) opt out via `allowSaveWithPendingChanges`.
  const isSaveDisabledByPendingChanges =
    pendingScopeToExecute != null && !allowSaveWithPendingChanges;

  const disableSaveAs = React.useMemo(
    () =>
      (!isNewScope &&
        (!selectedScope?.value?.editable ||
          !isValid ||
          (selectedScope?.value?.id && !isDirty))) ||
      (isNewScope && !isValid) ||
      noConditions ||
      isSaveDisabledByPendingChanges ||
      nonSelectableScopesFields?.some((field: string) => usedFields.has(field)),
    [
      isNewScope,
      selectedScope,
      isValid,
      isDirty,
      noConditions,
      isSaveDisabledByPendingChanges,
      usedFields,
    ]
  );

  const disableSave = React.useMemo(
    () => isNewScope || disableSaveAs || noConditions,
    [isNewScope, disableSaveAs, noConditions]
  );

  const saveTooltipContent = isSaveDisabledByPendingChanges
    ? "Apply your changes before saving"
    : undefined;

  const disableAddCondition = React.useMemo(
    () =>
      !isValid ||
      !controlledFields?.[controlledFields?.length - 1]?.conditions?.length,
    [isValid, controlledFields]
  );

  const resetHandler = () => {
    reset();
    // Cancel reverts to the server-saved scope and clears pending state.
    // Also set scopeToExecute to the clean server-saved query so the backend
    // query fires and results snap back to the server-saved state immediately.
    if (isNewScope) {
      // For a new (unsaved) scope, cancel navigates back to the default scope
      // so that skipped queries (which gate on scopeToExecute) can fire again.
      const defaultQuery = defaultScope?.value?.query ?? null;
      updateScopeState({
        selectedScope: defaultScope,
        scopeToExecute: defaultQuery,
        pendingScopeToExecute: null,
        isNewScope: false,
      });
      if (defaultScope) {
        localStorage.setItem(
          LS_SCOPE,
          JSON.stringify({ ...defaultScope, scopeToExecute: defaultQuery })
        );
      }
      return;
    }
    const cleanQuery = selectedScope?.value?.id
      ? (selectedScope?.value?.query ?? null)
      : null;
    updateScopeState({
      scopeToExecute: cleanQuery,
      pendingScopeToExecute: undefined,
    });
    if (selectedScope) {
      localStorage.setItem(
        LS_SCOPE,
        JSON.stringify({ ...selectedScope, scopeToExecute: cleanQuery })
      );
    }
  };

  const allowEditConditions =
    isAllowed(userActionsEnum.EDIT_SCOPE) ||
    (isNewScope && isAllowed(userActionsEnum.SOFT_EDIT_SCOPE));

  return (
    <div className={`scope-settings-wrapper`}>
      <FormProvider {...methods}>
        <form
          className={`scopes-panel-form-wrapper scopes-panel-form-wrapper-FormProvider`}
          onSubmit={handleSubmit(saveScope)}
        >
          <ConditionRowsPanel
            showSaveButtons={isAllowed(userActionsEnum.CREATE_SCOPE)}
            isSaving={isSaving}
            disableSave={disableSave}
            disableSaveAs={disableSaveAs}
            saveTooltipContent={saveTooltipContent}
            isDirty={isDirty}
            reset={resetHandler}
            saveAs={openSaveAsModal}
            previewFilter={previewFilter}
            onAddConditions={addCondition}
            disableAddCondition={disableAddCondition}
            controlledFields={controlledFields}
            remove={remove}
            showCancelButton={
              (!isNewScope && isAllowed(userActionsEnum.EDIT_SCOPE)) ||
              (isNewScope && isAllowed(userActionsEnum.SOFT_EDIT_SCOPE))
            }
            selectedId={selectedScope?.value?.id}
            fieldsData={filterFieldsData
              ?.filter(
                (item: FieldData) => !readOnlyFields?.includes(item.field)
              )
              .map((item: FieldData) => ({
                ...item,
                disabled: nonSelectableScopesFields?.includes(item.field),
                warning: nonSelectableScopesFields?.includes(item.field)
                  ? "This field is a readonly field and cannot be edited, used, or saved as a new scope"
                  : undefined,
              }))}
            isEditable={
              (selectedScope?.value?.editable || isNewScope) &&
              allowEditConditions
            }
            context={filterTypes.SCOPE}
            location={"scope-management"}
            showAddValue={
              (selectedScope?.value?.editable || isNewScope) &&
              allowEditConditions
            }
            showDelete={
              isAllowed(userActionsEnum.DELETE_SCOPE) ||
              (isNewScope && isAllowed(userActionsEnum.SOFT_EDIT_SCOPE))
            }
            shouldBlinkField={shouldBlinkField}
            shouldOpenField={undefined}
            updateFilter={updateScopeState}
            showAddCondition={allowEditConditions}
            allowRemoveAllConditions={isNewScope}
            onLastConditionRowRemove={resetToDefaultScope}
            chipPopoverClassName="scope-popover-wrap"
            onEditHandler={() => {}}
            showEditButton={false}
            statusesData={[]}
            filtersToPin={undefined}
            unsupportedFilterFields={[]}
            addUsedField={addUsedField}
            removeUsedField={removeUsedField}
          />
        </form>
      </FormProvider>
    </div>
  );
};

export default ScopeSettings;
